<script setup lang="ts">
import package_info from "@/../package.json";
import { useStation } from "@/store/station";
import { useContactOverlay } from "@/store/overlays/contact-overlay";
import { useI18n } from 'vue-i18n';
import { ref, watch } from "vue";

const station_store = useStation();
const contact_overlay = useContactOverlay();
const { locale } = useI18n({ useScope: 'global' })
const { t } = useI18n();
const version = package_info.version;
const num_questions = 8;
const questions = ref<{q: string, a: string}[]>([])
questions.value = Array.from({ length: num_questions }, (_, i) => ({
  q: t(`q${i + 1}`),
  a: t(`a${i + 1}`),
}));
questions.value.push({q: "CONTATTI", a: ""})

watch(locale, () => {
  questions.value = Array.from({ length: num_questions }, (_, i) => ({
    q: t(`q${i + 1}`),
    a: t(`a${i + 1}`),
  }));
  questions.value.push({q: "CONTATTI", a: ""})
});

</script>

<template>
  <v-dialog
    v-model="contact_overlay.is_visible"
    no-click-animation
    hide-overlay
    persistent
    z-index="925"
    style="background-color: rgb(255, 255, 255)"
  >
    <v-card>
      <v-card-title class="title text-center text-uppercase font-weight-bold text-secondary pb-0">
        {{ t("faq") }}
      </v-card-title>
      <v-card-text>
        <v-expansion-panels multiple>
          <v-virtual-scroll
            :items="questions"
            height="400"
          >
            <template v-slot:default="{ item }">
              <v-expansion-panel v-if="item.q != 'CONTATTI'" :rounded="0">
                <v-expansion-panel-title class="text-center">
                    <b>{{ item.q }}</b>
                </v-expansion-panel-title>
                <v-expansion-panel-text class="text-center">
                  {{ item.a }}
                </v-expansion-panel-text>
              </v-expansion-panel>
              <v-card v-else variant="flat" :rounded="0">
                <v-card-title class="title text-center text-uppercase font-weight-bold text-secondary">
                  {{ t("contacts") }}
                </v-card-title>
                <v-card-text>
                  <v-row>
                    <v-col class="text-secondary">
                      <v-row class="justify-center my-1">
                        <v-icon small>mdi-phone</v-icon>
                        <div>{{ t("phone") }}: +33 3 74 09 01 05</div>
                      </v-row>
                      <v-row class="justify-center my-1">
                        <v-icon small>mdi-email</v-icon>
                        <div>{{ t("email") }}: info@rossinienergy.com</div>
                      </v-row>
                      <v-row class="justify-center my-1">
                        <v-icon small>mdi-alarm</v-icon>
                        <div>{{ t("schedule") }}: 9h/18h</div>
                      </v-row>
                      <v-row class="justify-center my-1">
                        <div>charge.re version {{ version }}</div>
                      </v-row>
                      <v-row v-if="station_store.qrcode !== null" class="justify-center mt-1">
                        <v-icon small>mdi-qrcode</v-icon>
                        <div>QR code: {{ station_store.qrcode }}</div>
                      </v-row>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </template>
          </v-virtual-scroll>
        </v-expansion-panels>
      </v-card-text>
      <v-card-actions class="justify-center">
        <v-btn color="success" variant="elevated" @click="contact_overlay.hide">
          {{ t("close") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<i18n lang="yaml">
en:
  contacts: 'Contact'
  phone: 'Phone'
  email: 'Email'
  schedule: 'Schedule'
  close: 'Close'
  faq: "FAQ"
  q1: "My cable is stuck, what should I do?"
  a1: "If your cable is stuck in the socket, first try to push it all the way into the socket, then unplug your vehicle. You should hear a small click, a sign that the lock is lifting. You can then remove your cable. If the problem persists, don't hesitate to contact our maintenance service."
  q2: "I'm using an iPhone and I can't see the button to start charging, what should I do?"
  a2: "Make sure your phone is in portrait mode, the button should then appear. Or check that you haven't zoomed in on the screen."
  q3: "I can't enter my bank details, what should I do?"
  a3: "You can enter your bank card number next to the credit card icon. Or use ApplePay/GooglePay."
  q4: "Why was I charged a high amount for the top-up?"
  a4: "We make an imprint on your card (typically €60). You will then be credited with this amount, minus the amount of your top-up. There is a delay of about 3 days between these two operations; it works in a similar way to an automatic petrol pump."
  q5: "I've been debited even though I haven't topped up, why?"
  a5: "The full amount of your direct debit will be systematically refunded to you within approximately 3 days. Please write to info{'@'}rossinienergy.com to report a contrary case."
  q6: "I can't scan the QR code with my smartphone application. What should I do?"
  a6: "You can simply type the terminal's web address into your browser. It's in the form: ‘charge.re/XXXX’, with XXXX the terminal number. Everything is written on the QR code."
  q7: "I get adverts when I scan the QR code, why?"
  a7: "A : We don't carry any advertising for any product. If you see adverts when you scan our QR code, it's likely that the application you're using to scan it is systematically displaying them. To avoid this, choose a free or open source application, for example."
  q8: "The landing site is not in the form ‘charge.re/XXXXX’, can I trust it?"
  a8: "NO, this is a fraud. No page that is not in the form ‘charge.re/XXXXX’ will be able to activate the terminal, please do not insert your credit card numbers."
it:
  contacts: 'Contatti'
  phone: 'Telefono'
  email: 'Email'
  schedule: 'Orari di apertura'
  close: 'Chiudi'
  faq: "Domande frequenti"
  q1: "Il mio cavo è bloccato, cosa devo fare?"
  a1: "Se il cavo è incastrato nella presa, provare a spingerlo fino in fondo, quindi scollegare il veicolo. Si dovrebbe sentire un piccolo clic, segno che la serratura si sta sollevando. A questo punto è possibile rimuovere il cavo. Se il problema persiste, non esitate a contattare il nostro servizio di manutenzione."
  q2: "Sto usando un iPhone e non riesco a vedere il pulsante per avviare la ricarica, cosa devo fare?"
  a2: "Assicuratevi che il telefono sia in modalità verticale, il pulsante dovrebbe apparire. Oppure controllare di non aver ingrandito lo schermo."
  q3: "Non riesco a inserire i miei dati bancari, cosa devo fare?"
  a3: "È possibile inserire il numero della carta di credito accanto all'icona della carta di credito. Oppure utilizzare ApplePay/GooglePay."
  q4: "Perché mi è stato addebitato un importo elevato per la ricarica?"
  a4: "La carta viene impressa (in genere 60 euro). L'importo viene poi accreditato, meno l'importo della ricarica. Tra queste due operazioni c'è un ritardo di circa 3 giorni; il funzionamento è simile a quello di una pompa di benzina automatica."
  q5: "Mi è stato addebitato un importo anche se non ho effettuato una ricarica, perché?"
  a5: "L'intero importo del vostro addebito diretto vi sarà sistematicamente rimborsato entro circa 3 giorni. Scrivete a info{'@'}rossinienergy.com per segnalare un caso contrario."
  q6: "Non riesco a scansionare il codice QR con il mio smartphone, cosa devo fare?"
  a6: "È sufficiente digitare l'indirizzo web del terminale nel browser. È nella forma: “charge.re/XXXX”, con XXXX il numero del terminale. Tutto è scritto sul codice QR."
  q7: "Quando scannerizzo il codice QR ricevo pubblicità, perché?"
  a7: "Non facciamo pubblicità a nessun prodotto. Se vedete pubblicità quando scansionate il nostro codice QR, è probabile che l'applicazione che state usando per scansionarlo la visualizzi sistematicamente. Per evitare questo problema, scegliete un'altra applicazione gratuita o open source, ad esempio."
  q8: "Il sito di destinazione non ha la forma “charge.re/XXXXX”, posso fidarmi?"
  a8: "NO, potrebbe essere un tentativo di frode. Nessuna pagina che non abbia la forma “charge.re/XXXXX” sarà in grado di attivare il terminale, non inserite i numeri delle vostre carte di credito."
de:
  contacts: 'Wie man uns kontaktiert'
  phone: 'Telefonnummer'
  email: 'Email'
  schedule: 'Öffnungszeit'
  close: 'Schluss'
  faq: "FAQ"
  q1: "F: Mein Kabel steckt fest, was soll ich tun?"
  a1: "Wenn Ihr Kabel in der Buchse festsitzt, versuchen Sie zunächst, es ganz in die Buchse zu schieben, und ziehen Sie dann den Stecker Ihres Fahrzeugs ab. Sie sollten ein leises Klicken hören, ein Zeichen dafür, dass sich das Schloss löst. Dann können Sie das Kabel herausziehen. Wenn das Problem weiterhin besteht, zögern Sie nicht, unseren Wartungsdienst zu kontaktieren."
  q2: "Ich benutze ein iPhone und kann die Taste zum Starten des Ladevorgangs nicht sehen, was soll ich tun?"
  a2: "Vergewissern Sie sich, dass sich Ihr Telefon im Hochformat befindet, dann sollte die Schaltfläche erscheinen. Oder stellen Sie sicher, dass Sie den Bildschirm nicht vergrößert haben."
  q3: "Ich kann meine Bankdaten nicht eingeben, was soll ich tun?"
  a3: "Sie können Ihre Bankkartennummer neben dem Kreditkartensymbol eingeben. Oder verwenden Sie ApplePay/GooglePay."
  q4: "Warum wurde mir ein hoher Betrag für die Aufladung berechnet?"
  a4: "Wir machen einen Abdruck auf Ihrer Karte (normalerweise 60 €). Dieser Betrag wird Ihnen dann gutgeschrieben, abzüglich des Betrags Ihrer Aufladung. Zwischen diesen beiden Vorgängen liegt eine Verzögerung von etwa 3 Tagen; es funktioniert ähnlich wie bei einer automatischen Zapfsäule."
  q5: "Ich wurde belastet, obwohl ich nicht aufgeladen habe, warum?"
  a5: "Der volle Betrag Ihrer Lastschrift wird Ihnen innerhalb von etwa 3 Tagen systematisch erstattet. Bitte schreiben Sie an info{'@'}rossinienergy.com, um einen gegenteiligen Fall zu melden."
  Q6: "Ich kann den QR-Code mit meiner Smartphone-Anwendung nicht scannen. Was soll ich tun?"
  a6: "Sie können einfach die Webadresse des Terminals in Ihren Browser eingeben. Sie hat die Form: 'charge.re/XXXX', wobei XXXX die Terminalnummer ist. Alles ist auf dem QR-Code vermerkt."
  q7: "Ich bekomme Werbung, wenn ich den QR-Code scanne, warum?"
  a7: "Wir machen keine Werbung für irgendein Produkt. Wenn Sie beim Scannen unseres QR-Codes Werbung sehen, ist es wahrscheinlich, dass die Anwendung, die Sie zum Scannen verwenden, diese systematisch anzeigt. Um dies zu vermeiden, wählen Sie zum Beispiel eine kostenlose oder Open-Source-Anwendung."
  q8: "Die Landing Site hat nicht die Form ‚charge.re/XXXXX‘, kann ich ihr vertrauen?"
  a8: "NEIN, das ist ein Betrug. Keine Seite, die nicht die Form 'charge.re/XXXXX' hat, kann das Terminal aktivieren, bitte geben Sie Ihre Kreditkartennummern nicht ein."
fr:
  contacts: 'Contact'
  phone: 'Téléphone'
  email: 'Email'
  schedule: 'Horaires'
  close: 'Fermer'
  faq: "FAQ"
  q1: "Mon câble est bloqué, que dois-je faire ?"
  a1: "Si votre câble est coincé dans la prise, essayez d’abord de le pousser au fond de la prise, puis débranchez votre véhicule. Vous devriez entendre un petit claquement, signe que le verrou se lève. Vous pouvez alors retirer votre câble. Si le problème persiste, n’hésitez pas à contacter notre service de maintenance."
  q2: "J’utilise un iPhone et je ne vois pas le bouton pour démarrer la charge, que faire ?"
  a2: "Assurez-vous que votre téléphone est en mode portrait, le bouton devrait alors s’afficher. Ou vérifier de ne pas avoir fait un zoom sur l’écran."
  q3: "Je n’arrive pas à entrer mes coordonnées bancaires, comment faire ?"
  a3: "Vous pouvez entrer votre numéro de carte bancaire à côté de l’icône de la carte bleue. Ou utiliser ApplePay/GooglePay."
  q4: "Pourquoi ai-je été débité d’un montant élevé pour la recharge ?"
  a4: "Nous effectuons une empreinte sur votre carte (typiquement de 60€). Vous êtes ensuite recrédité de cette somme, moins le montant de votre recharge. Il y a un délai d’environ 3 jours entre ces deux opérations; le fonctionnement est similaire à celui d’une pompe à essence automatique."
  q5: "J’ai été débité alors que je ne me suis pas chargé, pourquoi ?"
  a5: "Le montant total de votre prélèvement vous sera systématiquement reversé dans un délai d’environ 3 jours. Veuillez écrire à info{'@'}rossinienergy.com pour signaler un cas contraire."
  q6: "Je n’arrive pas à scanner le QR code avec mon application sur smartphone, que faire ?"
  a6: "Vous pouvez simplement taper l’adresse web de la borne sur votre navigateur. Elle est de la forme : “charge.re/XXXX”, avec XXXX le numéro de la borne. Tout est écrit sur le QR code."
  q7: "J’ai des publicités lorsque je scanne le QR code, pourquoi ?"
  a7: "Nous ne relayons aucune publicité pour aucun produit. Si vous voyez des publicités s’afficher lorsque vous scannez notre QR code, il est probable que l’application que vous utilisez pour scanner vous en propose systématiquement. Pour vous en affranchir, optez pour une application libre ou open source par exemple."
  q8: "Le site d’atterrissage n’est pas de la forme “charge.re/XXXXX”, puis je faire confiance?"
  a8: "NON, il s’ait dans ce cas là d’une fraude. aucune page qui ne soit pas de la forme “charge.re/XXXXX” pourra activer la borne, veuillez ne pas insérer vos numéros de carte de crédit."
nl:
  contacts: 'Contact'
  phone: 'Telefoon'
  email: 'Email'
  schedule: 'Uren'
  close: 'Sluiten'
  faq: "FAQ"
  q1: "Mijn kabel zit vast, wat moet ik doen?"
  a1: "Als je kabel vastzit in de aansluiting, probeer hem dan eerst helemaal in de aansluiting te duwen en trek vervolgens de stekker uit het stopcontact. Je zou een kleine klik moeten horen, een teken dat het slot omhoog komt. Vervolgens kun je de kabel verwijderen. Als het probleem aanhoudt, aarzel dan niet om contact op te nemen met onze onderhoudsdienst."
  q2: "Ik gebruik een iPhone en ik kan de knop om het opladen te starten niet zien, wat moet ik doen?"
  a2: "Zorg ervoor dat je telefoon in portretmodus staat, de knop zou dan moeten verschijnen. Of controleer of je niet hebt ingezoomd op het scherm."
  q3: "Ik kan mijn bankgegevens niet invoeren, wat moet ik doen?"
  a3: "Je kunt je bankpasnummer invoeren naast het creditcard-icoontje. Of gebruik ApplePay/GooglePay."
  q4: "Waarom werd er een hoog bedrag in rekening gebracht voor de opwaardering?"
  a4: "We maken een afdruk op je kaart (meestal €60). Je krijgt dan dit bedrag gecrediteerd, min het bedrag van je opwaardering. Tussen deze twee handelingen zit een vertraging van ongeveer 3 dagen; het werkt op dezelfde manier als een automatische benzinepomp."
  q5: "Ik ben gedebiteerd terwijl ik niet heb opgewaardeerd, waarom?"
  a5: "Het volledige bedrag van je domiciliëring wordt systematisch terugbetaald binnen ongeveer 3 dagen. Schrijf naar info{'@'}rossinienergy.com om een tegenstrijdig geval te melden."
  q6: "Ik kan de QR-code niet scannen met mijn smartphone-applicatie. Wat moet ik doen?"
  a6: "Je kunt gewoon het webadres van de terminal in je browser typen. Het heeft de vorm: 'charge.re/XXXX', met XXXX het terminalnummer. Alles staat op de QR code."
  q7: "Ik krijg advertenties als ik de QR code scan, waarom?"
  a7: "We maken geen reclame voor welk product dan ook. Als je advertenties ziet wanneer je onze QR code scant, is het waarschijnlijk dat de applicatie die je gebruikt om de QR code te scannen deze systematisch weergeeft. Om dit te vermijden kun je bijvoorbeeld een gratis of open source applicatie kiezen."
  q8: "De landingssite is niet in de vorm ‘charge.re/XXXXX’, kan ik deze vertrouwen?"
  a8: "NEE, dit is fraude. Geen enkele pagina die niet de vorm 'charge.re/XXXXX' heeft, zal de terminal kunnen activeren, voer alstublieft geen creditcardnummers in."
</i18n>

<style scoped lang="scss">
</style>

<!-- vim: softtabstop=2:shiftwidth=2
-->
